import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

export type Preset = "24h" | "7d" | "30d" | "90d" | "qtd" | "ytd" | "custom";

export const PRESETS: { key: Preset; label: string }[] = [
  { key: "24h", label: "24h" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "qtd", label: "QTD" },
  { key: "ytd", label: "YTD" },
  { key: "custom", label: "Custom" },
];

interface DateRangeState {
  preset: Preset;
  start: string;
  end: string;
  compare: boolean;
  setPreset: (p: Preset) => void;
  setCustom: (start: string, end: string) => void;
  toggleCompare: () => void;
  /** Query params merged into list/analytics requests. */
  params: Record<string, string>;
}

const Ctx = createContext<DateRangeState>(null as never);

export function DateRangeProvider({ children }: { children: ReactNode }) {
  const [preset, setPresetState] = useState<Preset>("30d");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [compare, setCompare] = useState(false);

  function setPreset(p: Preset) {
    setPresetState(p);
  }
  function setCustom(s: string, e: string) {
    setStart(s);
    setEnd(e);
    setPresetState("custom");
  }

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (preset === "custom" && start && end) {
      // datetime-local -> ISO
      p.start = new Date(start).toISOString();
      p.end = new Date(end).toISOString();
    } else if (preset !== "custom") {
      p.range = preset;
    }
    if (compare) p.compare = "1";
    return p;
  }, [preset, start, end, compare]);

  const value: DateRangeState = {
    preset,
    start,
    end,
    compare,
    setPreset,
    setCustom,
    toggleCompare: () => setCompare((v) => !v),
    params,
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useDateRange = () => useContext(Ctx);

/** Build a query-string suffix from a params record. */
export function qs(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}
